/**
 * Confirm dialog — replaces bare window.confirm() calls across settings pages.
 *
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *
 *   async function handleDelete() {
 *     const yes = await confirm({
 *       title: 'Delete webhook?',
 *       message: 'This cannot be undone.',
 *       confirmLabel: 'Delete',
 *       danger: true,
 *     });
 *     if (!yes) return;
 *     // … proceed
 *   }
 *
 *   return (
 *     <>
 *       <button onClick={handleDelete}>Delete</button>
 *       <ConfirmDialog />
 *     </>
 *   );
 */
import { useCallback, useRef, useState } from 'react';
import { Modal } from './Modal';

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** If true, styles the confirm button as destructive */
  danger?: boolean;
}

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

export function useConfirm() {
  const [pending, setPending] = useState<ConfirmState | null>(null);
  // Use a ref so `confirm` closure is stable and doesn't need deps
  const pendingRef = useRef<ConfirmState | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const state: ConfirmState = { ...opts, resolve };
      pendingRef.current = state;
      setPending(state);
    });
  }, []);

  function handleResult(value: boolean) {
    pendingRef.current?.resolve(value);
    pendingRef.current = null;
    setPending(null);
  }

  function ConfirmDialog() {
    if (!pending) return null;
    return (
      <Modal onClose={() => handleResult(false)} aria-label={pending.title} maxWidth="max-w-sm">
        <div className="p-6 space-y-4">
          <h2 className="font-heading text-lg font-medium">{pending.title}</h2>
          {pending.message && <p className="text-sm text-muted-foreground">{pending.message}</p>}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => handleResult(false)}
              className="px-3 py-2 text-sm text-muted-foreground hover:text-foreground press"
            >
              {pending.cancelLabel ?? 'Cancel'}
            </button>
            <button
              type="button"
              onClick={() => handleResult(true)}
              className={[
                'rounded-md px-4 py-2 text-sm font-medium press',
                pending.danger
                  ? 'bg-destructive text-white hover:bg-destructive/90'
                  : 'bg-primary text-primary-foreground',
              ].join(' ')}
            >
              {pending.confirmLabel ?? 'Confirm'}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return { confirm, ConfirmDialog };
}
